import { Link, useLocation } from 'react-router-dom';
import { EnvelopeSimple, Phone, MapPin, Clock } from '@phosphor-icons/react';
import { useI18n } from '../context/I18nContext';
import { localizedPath } from '../i18n/routes';
import { openCookiePreferences } from './CookieConsent';
import { useCompanyInfo } from '../hooks/useCompanyInfo';
import { useBlockContent, cmsVal, safeCmsText, cleanCms } from '../hooks/useSiteContent';
import { isPublicSpvOfferEnabled } from '../config/featureFlags';
import WMLogo from './brand/WMLogo';

/**
 * Public footer — fully driven by the CMS block `block_footer`.
 *
 * Every visible element (logo, baseline, description, contact, columns,
 * risk banner, copyright) is editable in SiteEditor. Translation fallbacks
 * remain in place so the footer never renders blank when a field is empty.
 *
 * LOT 403-C — sanitize : retire les liens publics SPV / sponsor / porteur
 * quand `REACT_APP_PUBLIC_SPV_OFFER_ENABLED=false`. Les liens sont retirés
 * (1) au niveau LIENS individuels (ex : "Créer une SPV") et (2) au niveau
 * COLONNE entière si elle ne contient plus aucun lien après sanitisation.
 *
 * LOT Public Design A bis — Sanitization PII (Daniel 17/06/2026) :
 *   • Aucun email nominatif n'est jamais rendu côté public. Heuristique
 *     générique « local_part respecte la forme `prenom.nom` (2 mots ≥ 3
 *     lettres séparés par un point) » → rejet immédiat. Aucun nom propre
 *     n'est codé en dur dans la blocklist.
 *   • À la place, un CTA « Nous contacter » pointe vers la page /contact.
 *   • Letter-spacing footer fortement réduit (≤ 0.08em).
 */

// LOT 403-C — heuristique anti-leak. Mots-clés interdits côté public.
const SPV_FORBIDDEN_PATTERNS = [
  /créer\s+une\s+spv/i,
  /create\s+(?:an?\s+)?spv/i,
  /tarifs?\s+spv/i,
  /spv\s+pricing/i,
  /offre\s+spv/i,
  /administration\s+spv/i,
  /spv\s+administration/i,
  /\/(?:creer-spv|create-spv|creer-une-spv)/i,
  /soumettre.*projet/i,
  /submit.*project/i,
  /devenez?\s+sponsor/i,
  /become\s+(?:a\s+)?sponsor/i,
  /porteur\s+de\s+projet/i,
  /project\s+(?:owner|sponsor)s?/i,
  /raise\s+capital/i,
];

// LOT Public Design A bis — Heuristique GÉNÉRIQUE anti-PII pour les emails.
// Aucun nom propre n'est codé en dur dans l'heuristique : on ne rend pas un
// email dont la `local_part` ressemble à un alias nominatif `prenom.nom`.
//
// Passation du 30/10/2026 — Point de contact public validé par Daniel :
// Daniel a validé `line.medy@wealth-management-assets.com` comme point
// de contact PUBLIC officiel. On maintient une allowlist minimale (un seul
// email, exception traçable) pour autoriser cet affichage tout en
// préservant le rejet automatique des autres emails nominatifs (Daniel,
// Anissa, etc. restent masqués côté public).
const PUBLIC_APPROVED_NOMINATIVE_EMAILS = new Set([
  'line.medy@wealth-management-assets.com',
]);

function isNominativeEmail(email) {
  if (!email || typeof email !== 'string') return false;
  const normalized = email.trim().toLowerCase();
  // Allowlist explicite Daniel — bypass l'heuristique.
  if (PUBLIC_APPROVED_NOMINATIVE_EMAILS.has(normalized)) return false;
  const local = normalized.split('@')[0];
  if (!local) return false;
  // Heuristique : `mot.mot` (deux segments alpha ≥ 3 lettres, séparés par
  // un point, sans chiffre ni tiret). Couvre `prenom.nom`. Ne couvre pas
  // les alias d'équipe `contact`, `info`, `hello`, `relations-investisseurs`.
  return /^[a-z]{3,}\.[a-z]{3,}$/.test(local);
}

function isLinkSafePublic(link) {
  const label = (link?.label || '').trim();
  const href = (link?.href || '').trim();
  for (const pat of SPV_FORBIDDEN_PATTERNS) {
    if (pat.test(label) || pat.test(href)) return false;
  }
  return true;
}

function sanitizeColumns(columns) {
  if (!isPublicSpvOfferEnabled()) {
    return (columns || [])
      .map((col) => {
        // Filtre les liens individuels SPV/sponsor
        const safeLinks = (col?.links || []).filter(isLinkSafePublic);
        return { ...col, links: safeLinks };
      })
      // Retire les colonnes qui n'ont plus aucun lien
      .filter((col) => Array.isArray(col?.links) && col.links.length > 0);
  }
  return columns;
}

// DIRECTIVE PO (06/09/2026) — maillage « Notre équipe » garanti côté code
// (même philosophie que la colonne « Légal » hardcodée : indépendant d'un
// éventuel contenu CMS divergent en production). Insère le lien dans la
// colonne institutionnelle (celle qui contient « À propos »), juste après
// ce dernier ; ne fait rien si le lien est déjà présent.
function ensureTeamLink(columns, lang) {
  const teamHref = localizedPath('team', lang);
  const aboutHref = localizedPath('about', lang);
  const alreadyThere = (columns || []).some((col) =>
    (col?.links || []).some((lk) => (lk?.href || '').replace(/\/$/, '') === teamHref)
  );
  if (alreadyThere) return columns;
  const teamLink = { label: lang === 'fr' ? 'Notre équipe' : 'Our team', href: teamHref };
  return (columns || []).map((col) => {
    const links = col?.links || [];
    const aboutIdx = links.findIndex((lk) => (lk?.href || '').replace(/\/$/, '') === aboutHref);
    if (aboutIdx === -1) return col;
    const next = [...links];
    next.splice(aboutIdx + 1, 0, teamLink);
    return { ...col, links: next };
  });
}

export default function PublicFooter() {
  const currentYear = new Date().getFullYear();
  const { t, lang } = useI18n();
  const location = useLocation();
  const ci = useCompanyInfo();
  const { content: footer } = useBlockContent('block_footer');
  const { content: disclaimerBlock } = useBlockContent('block_disclaimers');

  const brand = footer?.sections?.brand || {};
  const contact = footer?.sections?.contact || {};
  const navColumns = footer?.sections?.nav_columns?.columns;
  const riskBanner = footer?.sections?.risk_banner || {};
  const bottomBar = footer?.sections?.bottom_bar || {};

  // MISSION CLOTURE SEO/GEO (P1) — le footer était 100 % <button onClick>
  // (aucun lien crawlable : 2 pages légales FR orphelines du graphe HTML,
  // légales EN invisibles). Les navigations internes sont désormais de VRAIS
  // <Link> (href présent dans le HTML sans JS). Comportements préservés :
  //   • scroll top : ScrollToTop global (App.js) sur changement de pathname ;
  //   • clic sur le lien de la page COURANTE : ScrollToTop ne se déclenche
  //     pas (pathname inchangé) → ce handler reproduit l'ancien scroll ;
  //   • blur : préservé (retrait du focus ring après clic).
  const samePathScroll = (href) => (e) => {
    e.currentTarget.blur();
    if (href && location.pathname === href) {
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    }
  };

  /** Fallback navigation columns that match the historical footer layout.
   *  Used only when the CMS has no columns yet. */
  const fallbackColumns = [
    {
      title: t('footer.quickLinks'),
      links: [
        { label: t('footer.aboutUs'), href: localizedPath('about', lang) },
        { label: lang === 'fr' ? 'Notre équipe' : 'Our team', href: localizedPath('team', lang) },
        { label: t('footer.howItWorksLink'), href: localizedPath('howItWorks', lang) },
        { label: lang === 'fr' ? 'Réalisations' : 'Track record', href: localizedPath('realisations', lang) },
        { label: lang === 'fr' ? 'Le journal' : 'The journal', href: localizedPath('journal', lang) },
        { label: t('footer.requestAccessLink'), href: localizedPath('requestAccess', lang) },
        { label: t('footer.investorLogin'), href: localizedPath('login', lang) },
      ],
    },
    {
      title: t('footer.legal'),
      links: [
        { label: t('footer.privacy'), href: localizedPath('privacy', lang) },
        { label: t('footer.terms'), href: localizedPath('terms', lang) },
        { label: t('footer.cookies'), href: localizedPath('cookies', lang) },
        { label: t('footer.mentionsLegales'), href: localizedPath('mentionsLegales', lang) },
      ],
    },
  ];

  const effectiveColumns = ensureTeamLink(
    sanitizeColumns(
      (Array.isArray(navColumns) && navColumns.length > 0) ? navColumns : fallbackColumns
    ),
    lang
  );

  // LOT Public Footer Legal Restore 20/06/2026 — Daniel P0.
  // La colonne « Légal » est désormais **toujours hardcoded** côté code
  // (jamais dépendante du CMS) pour garantir qu'elle ne disparaît pas
  // suite à un éventuel redesign du footer ou à une édition CMS partielle.
  // Les `href` sont calculés via `localizedPath()` → routes réelles
  // déclarées dans App.js, jamais cassées.
  // Si le CMS publie une colonne dont le titre matche (case-insensitive)
  // « légal / legal / mentions », elle est filtrée pour éviter le doublon.
  const isLegalColumnTitle = (title) => {
    const norm = (title || '').toString().toLowerCase().trim();
    return norm === 'légal' || norm === 'legal' || norm === 'mentions' ||
           norm.includes('mentions légales') || norm.includes('legal');
  };
  const cmsColumnsWithoutLegal = effectiveColumns.filter(
    (c) => !isLegalColumnTitle(c?.title)
  );
  const legalColumn = {
    title: lang === 'fr' ? 'Légal' : 'Legal',
    testid: 'footer-legal-column',
    links: [
      { label: t('footer.mentionsLegales'), href: localizedPath('mentionsLegales', lang), testid: 'footer-link-mentions-legales' },
      { label: t('footer.terms'), href: localizedPath('terms', lang), testid: 'footer-link-terms' },
      { label: t('footer.privacy'), href: localizedPath('privacy', lang), testid: 'footer-link-privacy' },
      { label: t('footer.cookies'), href: localizedPath('cookies', lang), testid: 'footer-link-cookies' },
      { label: t('footer.riskDisclosure'), href: localizedPath('riskDisclosure', lang), testid: 'footer-link-risk-disclosure-col' },
    ],
  };

  return (
    <footer
      className="bg-navy-950 text-ivory border-t border-gold/20"
      data-testid="public-footer"
      data-cms-section="block_footer"
      data-lot="public-design-A-septies"
    >
      <div className="container-premium py-16 lg:py-20">
        {/* LOT Public Footer Legal Restore 20/06/2026 — Daniel P0.
            Grille passe à 12 colonnes en desktop pour intégrer la colonne
            « Légal » (hardcoded, jamais dépendante du CMS) entre Services
            et Contact, tout en gardant Brand large + 2 colonnes CMS +
            Contact aérés. En mobile/tablette, le grid empile naturellement
            (1 col → 2 cols → 12 cols répartis). */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-10 lg:gap-10">
          {/* Brand */}
          <div className="lg:col-span-3" data-testid="footer-brand-block">
            <div className="mb-6">
              <WMLogo variant="dark" mode="horizontal" size="sm" data-testid="footer-logo" />
            </div>
            {brand.tagline && (
              <p className="text-[11px] font-medium uppercase tracking-[0.08em] text-gold mb-3" data-testid="footer-tagline">{safeCmsText(brand.tagline, brand.tagline)}</p>
            )}
            <p className="text-ivory/70 text-sm leading-relaxed" data-testid="footer-description">
              {safeCmsText(brand.description, cmsVal(footer, 'sections.description.content', t('footer.description')))}
            </p>
          </div>

          {/* Dynamic navigation columns from CMS (with safe fallback).
              On limite à 2 colonnes CMS (Plateforme + Services) car la 3ème
              de l'ancien CMS est désormais remplacée par la colonne « Légal »
              hardcoded ci-dessous. */}
          {cmsColumnsWithoutLegal.slice(0, 2).map((col, ci2) => (
            <div key={ci2} className="lg:col-span-2" data-testid={`footer-nav-col-${ci2}`}>
              <h4 className="text-[12px] uppercase tracking-[0.08em] font-medium text-gold mb-5">{col.title}</h4>
              <ul className="space-y-2.5">
                {(col.links || []).map((lk, li) => (
                  <li key={li}>
                    {lk.href?.startsWith('http') ? (
                      <a href={lk.href} target="_blank" rel="noreferrer" className="text-ivory/70 hover:text-ivory text-sm transition-colors">{lk.label}</a>
                    ) : lk.href ? (
                      <Link
                        to={lk.href}
                        onClick={samePathScroll(lk.href)}
                        className="text-ivory/70 hover:text-ivory text-sm transition-colors text-left"
                      >
                        {lk.label}
                      </Link>
                    ) : (
                      <span className="text-ivory/70 text-sm">{lk.label}</span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}

          {/* LOT Public Footer Legal Restore 20/06/2026 — Colonne « Légal »
              TOUJOURS rendue. Indépendante du CMS (anti-régression
              pérenne : si quelqu'un édite le CMS, la colonne reste). */}
          <div className="lg:col-span-2" data-testid={legalColumn.testid}>
            <h4 className="text-[12px] uppercase tracking-[0.08em] font-medium text-gold mb-5">{legalColumn.title}</h4>
            <ul className="space-y-2.5">
              {legalColumn.links.map((lk, li) => (
                <li key={li}>
                  <Link
                    to={lk.href}
                    onClick={samePathScroll(lk.href)}
                    className="text-ivory/70 hover:text-ivory text-sm transition-colors text-left"
                    data-testid={lk.testid}
                  >
                    {lk.label}
                  </Link>
                </li>
              ))}
              <li>
                <button
                  onClick={openCookiePreferences}
                  className="text-ivory/70 hover:text-ivory text-sm transition-colors text-left"
                  data-testid="footer-manage-cookies"
                >
                  {lang === 'fr' ? 'Gérer mes cookies' : 'Manage cookies'}
                </button>
              </li>
            </ul>
          </div>

          {/* Contact (CMS-driven with legacy fallback on useCompanyInfo, with PII sanitization) */}
          <div className="lg:col-span-3" data-testid="footer-contact-block">
            <h4 className="text-[12px] uppercase tracking-[0.08em] font-medium text-gold mb-5">{t('footer.contact')}</h4>
            <ul className="space-y-3.5">
              {(contact.address?.trim() || ci.address_line1) && (
                <li className="flex items-start gap-3">
                  <MapPin size={16} className="text-gold mt-0.5 flex-shrink-0" />
                  <span className="text-ivory/70 text-sm whitespace-pre-line leading-relaxed">
                    {contact.address?.trim() || `${ci.address_line1}\n${ci.address_line2}`}
                  </span>
                </li>
              )}
              {(contact.phone?.trim() || ci.phone) && (
                <li className="flex items-center gap-3">
                  <Phone size={16} className="text-gold flex-shrink-0" />
                  <a href={`tel:${(contact.phone || ci.phone || '').replace(/\s/g, '')}`} className="text-ivory/70 hover:text-ivory text-sm transition-colors">
                    {contact.phone?.trim() || ci.phone}
                  </a>
                </li>
              )}
              {/* LOT Public Design A bis — Aucun email rendu côté public.
                 Si le CMS fournit un alias d'équipe (`contact@`, `info@`),
                 on l'affiche ; sinon, le CTA « Nous contacter » suffit. */}
              {(() => {
                const raw = contact.email_general?.trim();
                if (!raw) return null;
                if (isNominativeEmail(raw)) return null;
                return (
                  <li className="flex items-center gap-3" data-testid="footer-contact-email-general">
                    <EnvelopeSimple size={16} className="text-gold flex-shrink-0" />
                    <a href={`mailto:${raw}`} className="text-ivory/70 hover:text-ivory text-sm transition-colors">{raw}</a>
                  </li>
                );
              })()}
              {(() => {
                const rawIR = contact.email_investor_relations;
                if (!rawIR || isNominativeEmail(rawIR)) return null;
                if (rawIR === contact.email_general) return null;
                return (
                  <li className="flex items-center gap-3" data-testid="footer-contact-email-ir">
                    <EnvelopeSimple size={16} className="text-gold flex-shrink-0" />
                    <a href={`mailto:${rawIR}`} className="text-ivory/70 hover:text-ivory text-sm transition-colors">{rawIR}</a>
                  </li>
                );
              })()}
              {contact.hours && (
                <li className="flex items-center gap-3">
                  <Clock size={16} className="text-gold flex-shrink-0" />
                  <span className="text-ivory/70 text-sm">{contact.hours}</span>
                </li>
              )}
              {/* CTA universel vers /contact — remplace l'affichage d'un email PII. */}
              <li className="pt-1">
                <Link
                  to={localizedPath('contact', lang)}
                  onClick={samePathScroll(localizedPath('contact', lang))}
                  className="inline-flex items-center text-[12.5px] tracking-[0.04em] uppercase font-medium text-gold hover:text-ivory transition-colors"
                  data-testid="footer-contact-cta"
                >
                  {lang === 'fr' ? 'Nous contacter' : 'Contact us'}
                </Link>
              </li>
            </ul>
          </div>
        </div>

        {/* Risk banner (CMS-driven) */}
        {(cleanCms(riskBanner.content)) && (
          <div className="border-t border-ivory/10 mt-14 pt-8" data-testid="footer-risk-banner">
            {riskBanner.overline && (
              <p className="text-[10.5px] font-medium uppercase tracking-[0.08em] text-gold mb-2">{safeCmsText(riskBanner.overline, riskBanner.overline)}</p>
            )}
            <p className="text-ivory/55 text-xs leading-relaxed max-w-4xl">{safeCmsText(riskBanner.content, riskBanner.content)}</p>
          </div>
        )}

        {/* Bottom bar */}
        <div className="border-t border-ivory/10 mt-8 pt-6 flex flex-col md:flex-row justify-between items-center gap-4" data-testid="footer-bottom-bar">
          <p className="text-ivory/50 text-xs">
            {safeCmsText(bottomBar.copyright, `© ${currentYear} ${t('footer.copyright')}`)}
          </p>
          {/* LOT Public Footer Legal Restore 20/06/2026 — Mini-liens discrets
              près du copyright (Mentions / CGU / Confidentialité). Cible
              demandée par Daniel : « ajouter dans la ligne basse, près du
              copyright, des liens plus discrets vers Mentions légales, CGU
              et Politique de confidentialité si nécessaire ». */}
          <div className="flex flex-wrap items-center justify-center md:justify-end gap-x-3 gap-y-2" data-testid="footer-bottom-legal-links">
            <Link
              to={localizedPath('mentionsLegales', lang)}
              onClick={samePathScroll(localizedPath('mentionsLegales', lang))}
              className="text-ivory/55 hover:text-ivory/85 text-[11px] tracking-[0.04em] uppercase transition-colors"
              data-testid="footer-bottom-link-mentions-legales"
            >
              {t('footer.mentionsLegales')}
            </Link>
            <span className="text-ivory/15">·</span>
            <Link
              to={localizedPath('terms', lang)}
              onClick={samePathScroll(localizedPath('terms', lang))}
              className="text-ivory/55 hover:text-ivory/85 text-[11px] tracking-[0.04em] uppercase transition-colors"
              data-testid="footer-bottom-link-terms"
            >
              {t('footer.terms')}
            </Link>
            <span className="text-ivory/15">·</span>
            <Link
              to={localizedPath('privacy', lang)}
              onClick={samePathScroll(localizedPath('privacy', lang))}
              className="text-ivory/55 hover:text-ivory/85 text-[11px] tracking-[0.04em] uppercase transition-colors"
              data-testid="footer-bottom-link-privacy"
            >
              {t('footer.privacy')}
            </Link>
            <span className="text-ivory/15">·</span>
            <Link to={localizedPath('riskDisclosure', lang)} onClick={samePathScroll(localizedPath('riskDisclosure', lang))} className="text-ivory/55 hover:text-ivory/85 text-[11px] tracking-[0.04em] uppercase transition-colors" data-testid="footer-risk-disclosure">{t('footer.riskDisclosure')}</Link>
          </div>
        </div>
        {/* Legal notice (CMS-driven), preserved as a discreet line below
            the bottom bar to keep the bottom bar readable. */}
        <div className="mt-4">
          <p className="text-ivory/55 text-[10.5px] leading-relaxed max-w-4xl mx-auto md:mx-0 text-center md:text-left" data-testid="footer-legal-notice">
            {safeCmsText(bottomBar.legal_notice, cmsVal(disclaimerBlock, 'sections.general_disclaimer.content', t('footer.legalDisclaimer')))}
          </p>
        </div>
      </div>
    </footer>
  );
}
